import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import DebouncedOrderSearch from "../../../components/DebouncedOrderSearch";
import axios from "axios";
import { Policy, PolicyInfo, PolicyType } from "./types";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  updateData: () => void;
  orderNumber: string;
  policyToRevise: Policy | null;
  onCloseNewPolicyModal: () => void;
}

const NewPolicyModal = ({
  isOpen,
  setIsOpen,
  updateData,
  orderNumber,
  policyToRevise,
  onCloseNewPolicyModal
}: Props) => {
  const [selectedValue, setSelectedValue] = useState(orderNumber || "");
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [policiesOfOrder, setPoliciesOfOrder] = useState<PolicyInfo[]>([]);

  const handleCloseModal = () => {
    setIsLoading(false);
    setIsOpen(false);
    onCloseNewPolicyModal();
  };

  const getPoliciesOfOrder = async (value: any) => {
    setIsLoading(true);
    const { data } = await axios.get<PolicyInfo[]>(`proxy/api/orders/getpolicies?orderNumber=${value}`);
    setPoliciesOfOrder(data);
    setIsLoading(false);
  };

  const onSave = async () => {
    setIsDisabled(true);
    const result =
      policyToRevise !== null
        ? [
            {
              id: 0,
              orderNumber: selectedValue,
              uniqueIdentifier: policyToRevise.uniqueIdentifier,
              type: Number(policyToRevise?.type) === 2 ? PolicyType.Loan : PolicyType.Owners,
              position: policyToRevise.position,
              policyNumber: policyToRevise.policyNumber,
              revision: policyToRevise.revision + 1
            }
          ]
        : policiesOfOrder?.map((policy) => ({
            ...policy,
            id: 0,
            uniqueIdentifier: policy.guid,
            orderNumber: selectedValue,
            type: policy.type === "LoanPolicy" ? PolicyType.Loan : PolicyType.Owners
          }));
    await axios.post("/api/outstandingChecks/AddPolicies", result);
    updateData();
    handleCloseModal();
    setIsDisabled(false);
  };

  return (
    <div>
      <Dialog open={isOpen} onClose={handleCloseModal} fullWidth>
        <DialogTitle>{policyToRevise !== null ? "Add a Revised Policy" : "Add Policy"}</DialogTitle>
        <DialogContent sx={{ marginTop: "35px" }}>
          {!orderNumber && (
            <DebouncedOrderSearch
              value={selectedValue}
              textboxMargin="dense"
              onSelect={async (e: React.SyntheticEvent<Element, Event>, option: string | null, reason: string) => {
                setSelectedValue(option || "");
                if (option) {
                  getPoliciesOfOrder(option);
                }
              }}
            />
          )}
          {selectedValue &&
            (isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography marginBottom={2} marginTop={2}>
                  These policies of order # {selectedValue} will be added:
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="space-between" marginBottom={2}>
                  <Typography fontWeight={"bold"}>Policy Type</Typography>
                  <Typography fontWeight={"bold"}>Policy Number</Typography>
                </Stack>
                {policyToRevise !== null ? (
                  <Stack direction="row" spacing={2} marginTop={1} justifyContent="space-between">
                    <Typography>
                      {Number(policyToRevise?.type) === 2 ? "Loan " : "Owners "} {policyToRevise.position}
                    </Typography>
                    <Typography>{policyToRevise.policyNumber}</Typography>
                  </Stack>
                ) : (
                  policiesOfOrder?.map((policy, index) => {
                    const policyType = policy.type
                      ? `${policy.type} ${policy.position || ""}`.replace(/policy/i, "").trim()
                      : "Loan";
                    return (
                      <Stack key={index} direction="row" spacing={2} marginTop={1} justifyContent="space-between">
                        <Typography>{policyType}</Typography>
                        <Typography>{policy.policyNumber}</Typography>
                      </Stack>
                    );
                  })
                )}
              </>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button variant="contained" onClick={onSave} disabled={isDisabled}>
            {isDisabled ? <CircularProgress size={24} color="inherit" /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NewPolicyModal;
